import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ProjectType,
  GetProjectTypesResponse,
} from "../../models/Project.model";
import {
  getProjectTypes,
  createOrUpdateProjectType,
  deleteProjectType,
} from "../thunks/projectTypeThunks";

interface ProjectTypeState {
  // נתוני סוגי פרויקטים
  projectTypes: ProjectType[];
  loading: boolean;
  error: string | null;
  success: boolean; // ודא שהשדה קיים
  message: string; // ודא שהשדה קיים
  // פעולות (יצירה/עדכון)
  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: boolean;
  operationMessage: string;

  // מצב אתחול
  initialized: boolean;
}

const initialState: ProjectTypeState = {
  projectTypes: [],
  loading: false,
  error: null,
  success: false,
  message: "",
  operationLoading: false,
  operationError: null,
  operationSuccess: false,
  operationMessage: "",

  initialized: false,
};

const projectTypeSlice = createSlice({
  name: "projectTypes",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.operationError = null;
      state.operationMessage = "";
      state.operationSuccess = false;
    },
    resetProjectTypesState: (state) => {
      Object.assign(state, initialState);
    },
    clearProjectTypesOnCompanyChange: (state) => {
      state.projectTypes = [];
      state.error = null;
      state.operationError = null;
      state.operationMessage = "";
      state.operationSuccess = false;
      state.initialized = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Project Types
      .addCase(getProjectTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getProjectTypes.fulfilled,
        (state, action: PayloadAction<GetProjectTypesResponse>) => {
          state.loading = false;
          state.projectTypes = action.payload.projectTypes;
          state.initialized = true;
        }
      )
      .addCase(getProjectTypes.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch project types";
      })

      // Create/Update Project Type
      .addCase(createOrUpdateProjectType.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createOrUpdateProjectType.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
          state.message = "סוג הפרויקט נשמר בהצלחה";
          state.error = null;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;

        if (requestData.projectTypeID === 0) {
          // יצירה חדשה
          const newProjectType: ProjectType = {
            projectTypeID: action.payload.projectTypeID,
            projectTypeName: requestData.projectTypeName,
            projectTypeIsDefault: false,
            projectTypeImage: requestData.imageURL,
          };
          state.projectTypes.push(newProjectType);
        } else {
          // עדכון קיים
          const index = state.projectTypes.findIndex(
            (pt) => pt.projectTypeID === requestData.projectTypeID
          );
          if (index !== -1) {
            state.projectTypes[index] = {
              ...state.projectTypes[index],
              projectTypeName: requestData.projectTypeName,
              projectTypeImage: requestData.imageURL,
            };
          }
        }
      })
      .addCase(createOrUpdateProjectType.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to create/update project type";
      })

      // Delete Project Type
      .addCase(deleteProjectType.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteProjectType.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
              state.message = "סוג הפרויקט נמחק בהצלחה";

        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        state.projectTypes = state.projectTypes.filter(
          (pt) => pt.projectTypeID !== requestData.projectTypeID
        );
      })
      .addCase(deleteProjectType.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete project type";
      });
  },
});

export const {
  clearMessages,
  resetProjectTypesState,
  clearProjectTypesOnCompanyChange,
} = projectTypeSlice.actions;

// Selectors
export const selectProjectTypes = (state: { projectTypes: ProjectTypeState }) =>
  state.projectTypes.projectTypes;
export const selectProjectTypesLoading = (state: {
  projectTypes: ProjectTypeState;
}) => state.projectTypes.loading;
export const selectProjectTypesError = (state: {
  projectTypes: ProjectTypeState;
}) => state.projectTypes.error;
export const selectProjectTypesInitialized = (state: {
  projectTypes: ProjectTypeState;
}) => state.projectTypes.initialized;
export const selectProjectTypeOperation = (state: {
  projectTypes: ProjectTypeState;
}) => ({
  loading: state.projectTypes.operationLoading,
  error: state.projectTypes.operationError,
  success: state.projectTypes.operationSuccess,
  message: state.projectTypes.operationMessage,
});

export default projectTypeSlice.reducer;
